export interface FolderFavorite {
  siteId: string;
  path: string;
  name: string;
  isFolder: boolean;
}

interface FolderFavoritesSchema {
  version: 1;
  items: FolderFavorite[];
}

export const FAVORITES_STORAGE_KEY = 'filespace_folder_favorites';
export const MAX_FOLDER_FAVORITES = 100;
const MAX_SERIALIZED_LENGTH = 30000;

const MAX_SITE_ID_LENGTH = 512;
const MAX_PATH_LENGTH = 1024;
const MAX_NAME_LENGTH = 256;

function roamingSettings(): Office.RoamingSettings | null {
  if (typeof Office === 'undefined') return null;
  return Office.context?.roamingSettings || null;
}

function sanitizeFavorite(value: unknown): FolderFavorite | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<FolderFavorite>;
  if (
    typeof candidate.siteId !== 'string' ||
    typeof candidate.path !== 'string' ||
    typeof candidate.name !== 'string' ||
    !candidate.siteId ||
    !candidate.path ||
    !candidate.name ||
    candidate.siteId.length > MAX_SITE_ID_LENGTH ||
    candidate.path.length > MAX_PATH_LENGTH ||
    candidate.name.length > MAX_NAME_LENGTH
  ) {
    return null;
  }
  return {
    siteId: candidate.siteId,
    path: candidate.path,
    name: candidate.name,
    isFolder:
      typeof candidate.isFolder === 'boolean' ? candidate.isFolder : candidate.path.endsWith('/'),
  };
}

function utf8Size(value: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  return value.length * 3;
}

function parseSchema(value: unknown): FolderFavorite[] {
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }
  if (!parsed || typeof parsed !== 'object') return [];
  const schema = parsed as Partial<FolderFavoritesSchema>;
  if (schema.version !== 1 || !Array.isArray(schema.items)) return [];

  const seen = new Set<string>();
  const items: FolderFavorite[] = [];
  for (const raw of schema.items) {
    const favorite = sanitizeFavorite(raw);
    if (!favorite) continue;
    const key = `${favorite.siteId}|${favorite.path}`;
    if (seen.has(key)) continue;
    const candidate = JSON.stringify({ version: 1, items: [...items, favorite] });
    if (utf8Size(candidate) > MAX_SERIALIZED_LENGTH) break;
    seen.add(key);
    items.push(favorite);
    if (items.length === MAX_FOLDER_FAVORITES) break;
  }
  return items;
}

function schemaFor(favorites: FolderFavorite[]): FolderFavoritesSchema {
  return { version: 1, items: parseSchema({ version: 1, items: favorites }) };
}

export function loadFolderFavorites(): FolderFavorite[] {
  const settings = roamingSettings();
  if (settings) return parseSchema(settings.get(FAVORITES_STORAGE_KEY));

  try {
    return parseSchema(localStorage.getItem(FAVORITES_STORAGE_KEY));
  } catch {
    return [];
  }
}

export async function saveFolderFavorites(favorites: FolderFavorite[]): Promise<FolderFavorite[]> {
  const schema = schemaFor(favorites);
  const settings = roamingSettings();
  if (settings) {
    settings.set(FAVORITES_STORAGE_KEY, schema);
    await new Promise<void>((resolve, reject) => {
      settings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error?.message || 'Unable to save folder favorites'));
      });
    });
    return schema.items;
  }

  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(schema));
  } catch {
    throw new Error('Unable to save folder favorites');
  }
  return schema.items;
}
